import { lazy, Suspense, useMemo, useState } from "react";
import { Box, CircularProgress, ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { useIsAuthenticated } from "@azure/msal-react";
import { lightTheme, darkTheme } from "@/theme";
import { createAppRouter } from "@/routes";
import LoginPage from "@/pages/Login/LoginPage";
import LandingPage from "@/pages/Landing/LandingPage";
import AcceptInvitePage from "@/pages/Team/AcceptInvitePage";
import ForgotPasswordPage from "@/pages/Login/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/Login/ResetPasswordPage";
import TermsPage from "@/pages/Legal/TermsPage";
import PrivacyPage from "@/pages/Legal/PrivacyPage";

// Lazy-loaded: none of these are the critical first-paint path (that's
// LandingPage/LoginPage, kept eager above), so splitting them out keeps the
// entry bundle small - directly serves the Core Web Vitals concern raised in
// docs/SEO_AUDIT.md M2, and matches the same pattern already used for the
// authenticated app's routes (see routes/index.tsx).
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const FeaturesHubPage = lazy(() => import("@/pages/Marketing/FeaturesHubPage"));
const FeatureDetailPage = lazy(() => import("@/pages/Marketing/FeatureDetailPage"));
const PricingPage = lazy(() => import("@/pages/Marketing/PricingPage"));
const SecurityPage = lazy(() => import("@/pages/Marketing/SecurityPage"));
const AboutPage = lazy(() => import("@/pages/Marketing/AboutPage"));
const ContactPage = lazy(() => import("@/pages/Marketing/ContactPage"));
const ResourcesPage = lazy(() => import("@/pages/Marketing/ResourcesPage"));
const BlogIndexPage = lazy(() => import("@/pages/Marketing/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/Marketing/BlogPostPage"));
const ComparisonPage = lazy(() => import("@/pages/Marketing/ComparisonPage"));
// Trial restriction disabled for now - see the commented-out gate below.
// import TrialExpiredScreen from "@/pages/Billing/TrialExpiredScreen";
import { useLocalAuth } from "@/localAuth";
import { useTenantProfile } from "@/hooks/useAssets";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } }
});

function PageFallback() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress />
    </Box>
  );
}

/** Gates the authenticated app on trial status - a child of QueryClientProvider
 * so useTenantProfile's useQuery can actually see the client (a hook call in
 * the same component that creates the provider element can't). */
function AuthenticatedApp({ router }: { router: ReturnType<typeof createAppRouter> }) {
  const { data: tenant, isLoading } = useTenantProfile();

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Trial-expiration blocking disabled for now - comment this back in (and
  // the TrialExpiredScreen import above) once trial enforcement is planned
  // out properly. See also TenantResolutionClaimsTransformation.cs, which
  // no longer flips tenants to Expired in the first place.
  // if (tenant?.status === "Expired") return <TrialExpiredScreen />;
  void tenant;
  return <RouterProvider router={router} />;
}

export default function App() {
  const isSsoAuthenticated = useIsAuthenticated();
  const { isAuthenticated: isLocalAuthenticated } = useLocalAuth();
  const isAuthenticated = isSsoAuthenticated || isLocalAuthenticated;

  const [mode, setMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme-mode") as "light" | "dark") ?? "light"
  );

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("theme-mode", next);
      return next;
    });
  };

  const theme = mode === "light" ? lightTheme : darkTheme;
  const router = useMemo(() => createAppRouter(mode, toggleMode), [mode]);

  const path = window.location.pathname;

  // Public marketing pages - accessible regardless of authentication state
  // (mirrors how /terms and /privacy already worked). "/" itself is
  // deliberately excluded here since it shows the authenticated Dashboard
  // instead of the marketing homepage once signed in - see renderUnauthenticated.
  //
  // /login, /accept-invite, /forgot-password, and /reset-password are also
  // session-independent by design (an invite or password-reset link has to
  // work with no session at all, and /login has to work even when the
  // visitor already has a *different* session - e.g. the read-only demo's
  // "Start your own free account" link). These used to live only inside
  // renderUnauthenticated(), which is only reached when isAuthenticated is
  // false - so an already-authenticated visitor hitting /login fell through
  // to the authenticated app's own router instead, which has no /login route
  // and crashed with React Router's default, unstyled 404 error boundary.
  function matchPublicPage() {
    if (path === "/terms") return <TermsPage />;
    if (path === "/privacy") return <PrivacyPage />;
    if (path === "/features") return <FeaturesHubPage />;
    if (path.startsWith("/features/")) return <FeatureDetailPage />;
    if (path === "/pricing") return <PricingPage />;
    if (path === "/security") return <SecurityPage />;
    if (path === "/about") return <AboutPage />;
    if (path === "/contact") return <ContactPage />;
    if (path === "/resources") return <ResourcesPage />;
    if (path === "/blog") return <BlogIndexPage />;
    if (path.startsWith("/blog/")) return <BlogPostPage />;
    if (path.startsWith("/compare/")) return <ComparisonPage />;
    if (path === "/accept-invite") return <AcceptInvitePage />;
    if (path === "/forgot-password") return <ForgotPasswordPage />;
    if (path === "/reset-password") return <ResetPasswordPage />;
    if (path === "/login") return <LoginPage />;
    return null;
  }

  function renderUnauthenticated() {
    if (path === "/") return <LandingPage />;
    // Anything else (including an authenticated-app path like /assets or
    // /team visited with no session, e.g. a stale or shared link) used to
    // silently render LandingPage here - a real duplicate-content bug, since
    // every such URL served identical content at HTTP 200. See
    // docs/SEO_AUDIT.md finding C3.
    return <NotFoundPage />;
  }

  const publicPage = matchPublicPage();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          "@media print": {
            // The Sidebar/TopHeader chrome lives outside any single page's own
            // markup, so per-page "@media print: display none" rules alone
            // can't hide it - this is the one place that can, for every page.
            "body *": { visibility: "hidden" },
            ".print-only, .print-only *": { visibility: "visible" },
            ".print-only": { position: "absolute", top: 0, left: 0, width: "100%" }
          }
        }}
      />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <QueryClientProvider client={queryClient}>
          {publicPage ? (
            <Suspense fallback={<PageFallback />}>{publicPage}</Suspense>
          ) : isAuthenticated ? (
            <AuthenticatedApp router={router} />
          ) : (
            <Suspense fallback={<PageFallback />}>{renderUnauthenticated()}</Suspense>
          )}
        </QueryClientProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
